import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import axios from 'axios';
import { LanguageProvider } from './i18n/LanguageContext';
import './App.css';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import Home from './pages/Home';
import HomeV2 from './pages/HomeV2';
import Catalogue from './pages/Catalogue';
import ProductDetail from './pages/ProductDetail';
import Cart from './pages/Cart';
import Contact from './pages/Contact';
import Distributor from './pages/Distributor';
import Checkout from './pages/Checkout';
import OrderConfirmation from './pages/OrderConfirmation';
import LogoShowcase from './pages/LogoShowcase';
import AdminLogin from './pages/Admin/AdminLogin';
import ResetPassword from './pages/Admin/ResetPassword';
import CrowdPreview from './pages/CrowdPreview';
import SeoPreview from './pages/SeoPreview';
import QuoteWizard from './pages/QuoteWizard';
import CityPage from './pages/CityPage';
import CitiesIndex from './pages/CitiesIndex';
import { CITIES } from './data/cities';
import Analytics from './pages/Admin/Analytics';
import AdminLayout from './pages/Admin/AdminLayout';
import AdminDashboard from './pages/Admin/AdminDashboard';
import ProductsManager from './pages/Admin/ProductsManager';
import CollectionsManager from './pages/Admin/CollectionsManager';
import OrdersManager from './pages/Admin/OrdersManager';
import PaymentSettings from './pages/Admin/PaymentSettings';
import SecuritySettings from './pages/Admin/SecuritySettings';
import QuotesManager from './pages/Admin/QuotesManager';
import BlogManager from './pages/Admin/BlogManager';
import NewsletterManager from './pages/Admin/NewsletterManager';
import Blog from './pages/Blog';
import BlogArticle from './pages/BlogArticle';
import AvisPage from './pages/AvisPage';
import Gallery from './pages/Gallery';
import EventPage from './pages/EventPage';
import MediaManager from './pages/Admin/MediaManager';
import MediaValidation from './pages/Admin/MediaValidation';
import SocialSettings from './pages/Admin/SocialSettings';
import ReviewsManager from './pages/Admin/ReviewsManager';
import { Toaster } from './components/ui/toaster';

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    if (window.location.hash) return; // on laisse les ancres #section fonctionner
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [pathname]);
  return null;
};

// Mesure d'audience maison : une page vue, sans cookie ni traceur tiers
const TrackPageViews = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    if (pathname.startsWith('/admin') || pathname.startsWith('/apercu')) return;
    let session = sessionStorage.getItem('plSession');
    if (!session) {
      session = Math.random().toString(36).slice(2) + Date.now().toString(36);
      sessionStorage.setItem('plSession', session);
    }
    axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/public/track`, {
      path: pathname,
      referrer: document.referrer || '',
      session,
      lang: localStorage.getItem('partyledLang') || 'fr',
    }).catch(() => {});
  }, [pathname]);
  return null;
};

function App() {
  const [cartItemsCount, setCartItemsCount] = useState(0);

  useEffect(() => {
    // Update cart count
    const updateCartCount = () => {
      const cart = JSON.parse(localStorage.getItem('cart') || '[]');
      setCartItemsCount(cart.length);
    };

    updateCartCount();

    // Listen for storage changes
    window.addEventListener('storage', updateCartCount);
    
    // Custom event for cart updates
    const handleCartUpdate = () => updateCartCount();
    window.addEventListener('cartUpdated', handleCartUpdate);

    return () => {
      window.removeEventListener('storage', updateCartCount);
      window.removeEventListener('cartUpdated', handleCartUpdate);
    };
  }, []);

  return (
    <LanguageProvider>
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <TrackPageViews />
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={
            <>
              <Navbar cartItemsCount={cartItemsCount} />
              <HomeV2 />
              <Footer />
            </>
          } />
          <Route path="/accueil-original" element={
            <>
              <Navbar cartItemsCount={cartItemsCount} />
              <Home />
              <Footer />
            </>
          } />
          <Route path="/apercu-accueil" element={
            <>
              <Navbar cartItemsCount={cartItemsCount} />
              <HomeV2 />
              <Footer />
            </>
          } />
          <Route path="/catalogue" element={
            <>
              <Navbar cartItemsCount={cartItemsCount} />
              <Catalogue />
              <Footer />
            </>
          } />
          <Route path="/produit/:slug" element={
            <>
              <Navbar cartItemsCount={cartItemsCount} />
              <ProductDetail />
              <Footer />
            </>
          } />
          <Route path="/panier" element={
            <>
              <Navbar cartItemsCount={cartItemsCount} />
              <Cart />
              <Footer />
            </>
          } />
          <Route path="/contact" element={
            <>
              <Navbar cartItemsCount={cartItemsCount} />
              <Contact />
              <Footer />
            </>
          } />
          <Route path="/avis" element={
            <>
              <Navbar cartItemsCount={cartItemsCount} />
              <AvisPage />
              <Footer />
            </>
          } />
          <Route path="/blog" element={
            <>
              <Navbar cartItemsCount={cartItemsCount} />
              <Blog />
              <Footer />
            </>
          } />
          <Route path="/blog/:slug" element={
            <>
              <Navbar cartItemsCount={cartItemsCount} />
              <BlogArticle />
              <Footer />
            </>
          } />
          <Route path="/distributeur" element={
            <>
              <Navbar cartItemsCount={cartItemsCount} />
              <Distributor />
              <Footer />
            </>
          } />
          <Route path="/galerie" element={
            <>
              <Navbar cartItemsCount={cartItemsCount} />
              <Gallery />
              <Footer />
            </>
          } />
          <Route path="/evenements/:type" element={
            <>
              <Navbar cartItemsCount={cartItemsCount} />
              <EventPage />
              <Footer />
            </>
          } />
          <Route path="/checkout" element={
            <>
              <Navbar cartItemsCount={cartItemsCount} />
              <Checkout />
              <Footer />
            </>
          } />
          <Route path="/commande/:orderId" element={
            <>
              <Navbar cartItemsCount={cartItemsCount} />
              <OrderConfirmation />
              <Footer />
            </>
          } />
          <Route path="/logo" element={
            <>
              <Navbar cartItemsCount={cartItemsCount} />
              <LogoShowcase />
              <Footer />
            </>
          } />

          <Route path="/apercu-seo" element={<SeoPreview />} />

          <Route path="/devis" element={
            <>
              <Navbar />
              <QuoteWizard />
              <Footer />
            </>
          } />

          {CITIES.map((c) => (
            <Route key={c.slug} path={`/bracelets-led-${c.slug}`} element={
              <>
                <Navbar />
                <CityPage citySlug={c.slug} />
                <Footer />
              </>
            } />
          ))}

          <Route path="/villes" element={
            <>
              <Navbar />
              <CitiesIndex />
              <Footer />
            </>
          } />

          <Route path="/apercu-foule" element={
            <>
              <Navbar cartItemsCount={cartItemsCount} />
              <CrowdPreview />
              <Footer />
            </>
          } />

          {/* Admin Routes */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin/reset-password" element={<ResetPassword />} />
          <Route path="/admin" element={<AdminLayout />}>
            <Route path="dashboard" element={<AdminDashboard />} />
            <Route path="products" element={<ProductsManager />} />
            <Route path="collections" element={<CollectionsManager />} />
            <Route path="orders" element={<OrdersManager />} />
            <Route path="quotes" element={<QuotesManager />} />
            <Route path="audience" element={<Analytics />} />
            <Route path="blog" element={<BlogManager />} />
            <Route path="reviews" element={<ReviewsManager />} />
            <Route path="media" element={<MediaManager />} />
            <Route path="media/validation" element={<MediaValidation />} />
            <Route path="social" element={<SocialSettings />} />
            <Route path="newsletter" element={<NewsletterManager />} />
            <Route path="payment-settings" element={<PaymentSettings />} />
            <Route path="security" element={<SecuritySettings />} />
          </Route>
        </Routes>
        <Toaster />
      </BrowserRouter>
    </div>
    </LanguageProvider>
  );
}

export default App;